<template>
  <modal ref="modal" display="inline" modal-size="xl">
    <template #trigger>
      <a class="privacy-policy-link" tabindex="0" @click.stop="clickHandler" @keydown="keyDownHandler">
        Privacy Policy
      </a>
    </template>
    <template #title>
      <h4>Privacy Policy</h4>
    </template>
    <template #content>
      <padding direction="vertical" size="medium" class="privacy-policy">

        <p>
          This privacy policy applies between you, the User of this Website, and Beautynet Ltd, the owner and provider
          of
          this Website. Beautynet Ltd takes the privacy of your information very seriously. This privacy policy applies
          to
          our use of any and all Data collected by us or provided by you in relation to your use of the Website.
        </p>
        <p>
          <b>Please read this privacy policy carefully.</b>
        </p>

        <h4>
          Definitions and interpretation
        </h4>
        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">1.</span>In this privacy policy, the following definitions are
          used:
          <table>
            <tbody>
              <tr>
                <td>
                  <b>Data</b>
                </td>
                <td>
                  collectively all information that you submit to Beautynet Ltd via the Website. This definition
                  incorporates, where applicable, the definitions provided in the Data Protection Laws;
                </td>
              </tr>
              <tr>
                <td>
                  <b>Cookies</b>
                </td>
                <td>
                  a small text file placed on your computer by this Website when you visit certain parts of the Website
                  and
                  /or when you use certain features of the Website. Details of the cookies used by this Website are set
                  out
                  in the clause below (Cookies);
                </td>
              </tr>
              <tr>
                <td>
                  <b>Data Protection Laws</b>
                </td>
                <td>
                  any applicable law relating to the processing of personal Data, including but not limited to the GDPR,
                  and any national implementing and supplementary laws, regulations and secondary legislation;
                </td>
              </tr>
              <tr>
                <td>
                  <b>GDPR</b>
                </td>
                <td>
                  the UK General Data Protection Regulation;
                </td>
              </tr>
              <tr>
                <td>
                  <b>Beautynet Ltd, we</b> or <b>us</b>
                </td>
                <td>
                  Beautynet Ltd, a company incorporated in England and Wales with registered number 06357041
                  whose registered office is at Newminster House, 27-29 Baldwin St, Bristol, BS1 1LT;
                </td>
              </tr>
              <tr>
                <td>
                  <b>UK and EU Cookie Law</b>
                </td>
                <td>
                  the Privacy and Electronic Communications (EC Directive) Regulations 2003 as amended by the
                  Privacy and Electronic Communications (EC Directive) (Amendment) Regulations 2011 & the Privacy
                  and Electronic Communications (EC Directive) (Amendment) Regulations 2018;
                </td>
              </tr>
              <tr>
                <td>
                  <b>User</b> or <b>you</b>
                </td>
                <td>
                  any third party that accesses the Website and is not either (i) employed by Beautynet Ltd and acting
                  in
                  the course of their employment or (ii) engaged as a consultant or otherwise providing services to
                  Beautynet Ltd and accessing the Website in connection with the provision of such services; and
                </td>
              </tr>
              <tr>
                <td>
                  <b>Website</b>
                </td>
                <td>
                  the website that you are currently using, beautyneteurope.com, and any sub-domains of this site unless
                  expressly excluded by their own terms and conditions.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">2.</span>In this privacy policy, unless the context requires a
          different interpretation:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>the singular includes the plural and vice versa;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span>references to sub-clauses, clauses, schedules or
            appendices are to sub-clauses, clauses, schedules or appendices of this privacy policy;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">c.</span>a reference to a person includes firms,
            companies, government entities, trusts and partnerships;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">d.</span>"including" is understood to mean "including
            without limitation";
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">e.</span>reference to any statutory provision includes
            any modification or amendment of it;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">f.</span>the headings and sub-headings do not form part
            of this privacy policy.
          </div>
        </div>

        <h4>Scope of this privacy policy</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">3.</span>This privacy policy applies only to the actions of
          Beautynet Ltd and Users with respect to this Website. It does not extend to any websites that can be accessed
          from this Website including, but not limited to, any links we may provide to social media websites.
        </div>
        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">4.</span>For purposes of the applicable Data Protection Laws,
          Beautynet Ltd is the "data controller". This means that Beautynet Ltd determines the purposes for which, and
          the manner in which, your Data is processed.
        </div>

        <h4>Data collected</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">5.</span>We may collect the following Data, which includes
          personal Data, from you:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>name;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span>job title;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">c.</span>IP address (automatically collected);
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">d.</span>web browser type and version (automatically
            collected);
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">e.</span>operating system (automatically collected);
          </div>
          in each case, in accordance with this privacy policy.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">6.</span>We collect Data in the following ways:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>data is given to us by you; and
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span>data is collected automatically.
          </div>
        </div>

        <h4>Data that is given to us by you</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">7.</span>Beautynet Ltd will collect your Data in a number of
          ways, for example:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>when you contact us through the Website, by
            telephone, post, e-mail or through any other means;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span>when you register with us and set up an account
            to receive our products/services;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">c.</span>when you complete surveys that we use for
            research purposes (although you are not obliged to respond to them);
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">d.</span>when you use our services;
          </div>
          in each case, in accordance with this privacy policy.
        </div>

        <h4>Data that is collected automatically</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">8.</span>To the extent that you access the Website, we will
          collect your Data automatically, for example:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>we automatically collect some information about
            your visit to the Website. This information helps us to make improvements to Website content and navigation,
            and includes your IP address, the date, times and frequency with which you access the Website and the way
            you use and interact with its content.
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span>we will collect your Data automatically via
            cookies, in line with the cookie settings on your browser. For more information about cookies, and how we
            use them on the Website, see the section below, headed "Cookies".
          </div>
        </div>

        <h4>Our use of Data</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">9.</span>Any or all of the above Data may be required by us
          from time to time in order to provide you with the best possible service and experience when using our
          Website. Specifically, Data may be used by us for the following reasons:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>internal record keeping;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span>improvement of our products / services;
          </div>
          in each case, in accordance with this privacy policy.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">10.</span>We may use your Data for the above purposes if we
          deem it necessary to do so for our legitimate interests. If you are not satisfied with this, you have the
          right to object in certain circumstances (see the section headed "Your rights" below).
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">11.</span>When you register with us and set up an account to
          receive our services, the legal basis for this processing is the performance of a contract between you and us
          and/or taking steps, at your request, to enter into such a contract.
        </div>

        <h4>Who we share Data with</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">12.</span>We may share your Data with the following groups of
          people for the following reasons:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>our employees, agents and/or professional
            advisors - to process orders and address queries;
          </div>
          in each case, in accordance with this privacy policy.
        </div>

        <h4>Keeping Data secure</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">13.</span>We will use technical and organisational measures to
          safeguard your Data, for example:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span>access to your account is controlled by a
            password and a user name that is unique to you;
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span>we store your Data on secure servers.;
          </div>
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">14.</span>Technical and organisational measures include
          measures to deal with any suspected data breach. If you suspect any misuse or loss or unauthorised access to
          your Data, please let us know immediately by contacting us via this e-mail address: info@beautyneteurope.com.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">15.</span>If you want detailed information from Get Safe
          Online on how to protect your information and your computers and devices against fraud, identity theft,
          viruses and many other online problems, please visit www.getsafeonline.org. Get Safe Online is supported by HM
          Government and leading businesses.
        </div>

        <h4>Data retention</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">16.</span>Unless a longer retention period is required or
          permitted by law, we will only hold your Data on our systems for the period necessary to fulfil the purposes
          outlined in this privacy policy or until you request that the Data be deleted.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">17.</span>Even if we delete your Data, it may persist on
          backup or archival media for legal, tax or regulatory purposes.
        </div>

        <h4>Your rights</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">18.</span>You have the following rights in relation to your
          Data:
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">a.</span><b>Right to access</b> - the right to request
            (i) copies of the information we hold about you at any time, or (ii) that we modify, update or delete such
            information. If we provide you with access to the information we hold about you, we will not charge you for
            this, unless your request is "manifestly unfounded or excessive." Where we are legally permitted to do so,
            we may refuse your request. If we refuse your request, we will tell you the reasons why.
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">b.</span><b>Right to correct</b> - the right to have your
            Data rectified if it is inaccurate or incomplete.
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">c.</span><b>Right to erase</b> - the right to request
            that we delete or remove your Data from our systems.
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">d.</span><b>Right to restrict our use of your Data</b> -
            the right to "block" us from using your Data or limit the way in which we can use it.
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">e.</span><b>Right to data portability</b> - the right to
            request that we move, copy or transfer your Data.
          </div>
          <div class="privacy-policy__sublist-item">
            <span class="privacy-policy__sublist-item__letter">f.</span><b>Right to object</b> - the right to object to
            our use of your Data including where we use it for our legitimate interests
          </div>
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">19.</span>To make enquiries, exercise any of your rights set
          out above, or withdraw your consent to the processing of your Data (where consent is our legal basis for
          processing your Data), please contact us via this e-mail address: info@beautyneteurope.com.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">20.</span>If you are not satisfied with the way a complaint
          you make in relation to your Data is handled by us, you may be able to refer your complaint to the relevant
          data protection authority. For the UK, this is the Information Commissioner's Office (ICO). The ICO's contact
          details can be found on their website at https://ico.org.uk/.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">21.</span>It is important that the Data we hold about you is
          accurate and current. Please keep us informed if your Data changes during the period for which we hold it.
        </div>

        <h4>Links to other websites</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">22.</span>This Website may, from time to time, provide links
          to other websites. We have no control over such websites and are not responsible for the content of these
          websites. This privacy policy does not extend to your use of such websites. You are advised to read the
          privacy policy or statement of other websites prior to using them.
        </div>

        <h4>Changes of business ownership and control</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">23.</span>Beautynet Ltd may, from time to time, expand or
          reduce our business and this may involve the sale and/or the transfer of control of all or part of Beautynet
          Ltd. Data provided by Users will, where it is relevant to any part of our business so transferred, be
          transferred along with that part and the new owner or newly controlling party will, under the terms of this
          privacy policy, be permitted to use the Data for the purposes for which it was originally supplied to us.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">24.</span>We may also disclose Data to a prospective purchaser
          of our business or any part of it.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">25.</span>In the above instances, we will take steps with the
          aim of ensuring your privacy is protected.
        </div>

        <h4>Cookies</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">26.</span>This Website may place and access certain Cookies on
          your computer. Beautynet Ltd uses Cookies to improve your experience of using the Website and to improve our
          range of products. Beautynet Ltd has carefully chosen these Cookies and has taken steps to ensure that your
          privacy is protected and respected at all times.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">27.</span>All Cookies used by this Website are used in
          accordance with current UK and EU Cookie Law.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">28.</span>Before the Website places Cookies on your computer,
          you will be presented with a message bar requesting your consent to set those Cookies. By giving your consent
          to the placing of Cookies, you are enabling Beautynet Ltd to provide a better experience and service to you.
          You may, if you wish, deny consent to the placing of Cookies; however certain features of the Website may not
          function fully or as intended.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">29.</span>This Website may place the following Cookies:
          <table>
            <thead>
              <tr>
                <th>Type of Cookie</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Strictly necessary cookies
                </td>
                <td>
                  These are cookies that are required for the operation of
                  our website. They include, for example, cookies that
                  enable you to log into secure areas of our website, use a
                  shopping cart or make use of e-billing services.
                </td>
              </tr>
              <tr>
                <td>
                  Analytical/performance cookies
                </td>
                <td>
                  They allow us to recognise and count the number of
                  visitors and to see how visitors move around our website
                  when they are using it. This helps us to improve the way
                  our website works, for example, by ensuring that users
                  are finding what they are looking for easily.
                </td>
              </tr>
              <tr>
                <td>
                  Functionality cookies
                </td>
                <td>
                  These are used to recognise you when you return to our
                  website. This enables us to personalise our content for
                  you, greet you by name and remember your preferences
                  (for example, your choice of language or region). By
                  using the Website, you agree to our placement of
                  functionality cookie.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">30.</span>You can find a list of Cookies that we use in the
          Cookies Schedule.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">31.</span>You can choose to enable or disable Cookies in your
          internet browser. By default, most internet browsers accept Cookies but this can be changed. For further
          details, please see the help menu in your internet browser. You can switch off Cookies at any time, however,
          you may lose any information that enables you to access the Website more quickly and efficiently
          .
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">32.</span>You can choose to delete Cookies at any time;
          however, you may lose any information that enables you to access the Website more quickly and efficiently
          including, but not limited to, personalisation settings.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">33.</span>It is recommended that you ensure that your
          internet browser is up-to-date and that you consult the help and guidance provided by the developer of your
          internet browser if you are unsure about adjusting your privacy settings.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">34.</span>For more information generally on cookies,
          including how to disable them, please refer to aboutcookies.org. You will also find details on how to delete
          cookies from your computer.
        </div>

        <h4>General</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">35.</span>You may not transfer any of your rights under this
          privacy policy to any other person. We may transfer our rights under this privacy policy where we reasonably
          believe your rights will not be affected.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">36.</span>If any court or competent authority finds that any
          provision of this privacy policy (or part of any provision) is invalid, illegal or unenforceable, that
          provision or part-provision will, to the extent required, be deemed to be deleted, and the validity and
          enforceability of the other provisions of this privacy policy will not be affected.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">37.</span>Unless otherwise agreed, no delay, act or omission
          by a party in exercising any right or remedy will be deemed a waiver of that, or any other, right or remedy.
        </div>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">38.</span>This Agreement will be governed by and interpreted
          according to the law of England and Wales. All disputes arising under the Agreement will be subject to the
          exclusive jurisdiction of the English and Welsh courts.
        </div>

        <h4>Changes to this privacy policy</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">39.</span>Beautynet Ltd reserves the right to change this
          privacy policy as we may deem necessary from time to time or as may be required by law. Any changes will be
          immediately posted on the Website and you are deemed to have accepted the terms of the privacy policy on your
          first use of the Website following the alterations.
          <p>You may contact Beautynet Ltd by email at info@beautyneteurope.com.</p>
        </div>

        <h4>Attribution</h4>

        <div class="privacy-policy__list-item">
          <span class="privacy-policy__list-item__number">40.</span>This privacy policy was created using a document
          from Rocket Lawyer (https://www.rocketlawyer.com/gb/en).
        </div>

        <p>
          This privacy policy was created on <b>07 September 2022</b>.
        </p>

        <h4>Cookies</h4>

        Below is a list of the cookies that we use. We have tried to ensure this is complete and up to date, but if you
        think that we
        have missed a cookie or there is any discrepancy, please let us know

        <h5>Strictly necessary</h5>

        We use the following strictly necessary cookies:

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Session storage
              </td>
              <td>
                We use this session cookie to remember you and maintain your session whilst you are using our website
              </td>
            </tr>
          </tbody>
        </table>

        <h5>Functionality</h5>

        We use the following functionality cookies:

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Offer data
              </td>
              <td>
                We use this cookie to store data relating to the selected offer filters and the quantities entered so
                that they may be recalled in future sessions
              </td>
            </tr>
          </tbody>
        </table>

        <h5>Analytical/performance</h5>

        We use the following analytical/performance cookies:

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Site performance
              </td>
              <td>
                We use this cookie to improve the performance and responsiveness of the site
              </td>
            </tr>
          </tbody>
        </table>
      </padding>

      <modal-actions cancel-button-label="Close" hide-submit @cancel="$refs.modal.closeModal()"/>
    </template>
  </modal>
</template>

<script lang="js">
import Modal from '../Modal'
import ModalActions from '../ModalActions'
import Padding from './Padding'
import enterKeyPressed from '../../utils/enterKeyPressed'

export default {
  name: 'PrivacyPolicyModal',
  components: { ModalActions, Padding, Modal },
  methods: {
    keyDownHandler(event) {
      if (enterKeyPressed(event)) {
        this.clickHandler()
      }
    },
    clickHandler() {
      this.$refs.modal.openModal()
    },
  },
}
</script>
